import { Button } from '@creditornot/cb-button';
import { cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import React, { useContext, useState } from 'react';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import useMenuData from 'modules/menu/useMenuData';

import AddOptionsToItem from './AddOptionsToItem';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';
import { objectId } from '../../../options/utils';
import OptionBindingHeaderFormSection from '../OptionBinding/OptionBindingHeaderFormSection';
import { buildOptionBindingCreatePayload } from '../OptionBinding/utils';

type Props = {
  className?: string;
  isWeightedItem: boolean;
};

const Root = styled.div`
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  padding-block-end: 24px;
`;

const Header = styled.div`
  ${typographyCss.Title1};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const StyledInputAlert = styled(InputAlert)`
  margin: 26px 0;
`;

const AddButton = styled(Button).attrs({
  size: 'small',
  variant: 'lightBlue',
})`
  margin-block-start: 24px;
`;

export type FormValues = {
  optionBindings: OptionBinding[];
};

const OptionBindingsFormSection = ({ className, isWeightedItem }: Props) => {
  const intl = useIntl();
  const { fields } = useFieldArray<OptionBinding>('optionBindings');

  const { options } = useMenuData();

  const menuPermissions = useContext(MenuPermissionsContext);
  const [isItemOptionsModalOpen, setIsItemOptionsModalOpen] = useState(false);

  const getOptionFromOptionId = (optionId: string) => {
    return options.find(option => option.id === optionId);
  };

  const handleAddOptionBindings = (selectedOptionIds: Set<string>) => {
    const addedOptionBindings = [...selectedOptionIds].map(id => ({
      ...buildOptionBindingCreatePayload(getOptionFromOptionId(id)!),
      id: objectId(),
    }));
    addedOptionBindings.forEach(addedOptionBinding => fields.push(addedOptionBinding));
    setIsItemOptionsModalOpen(false);
  };

  const handleDeleteOptionBinding = (optionBindingId: string) => {
    const optionBindingToDelete = fields.value.find(optionBinding => optionBinding.id === optionBindingId);
    optionBindingToDelete && fields.remove(fields.value.indexOf(optionBindingToDelete));
  };

  return (
    <Root className={className}>
      <Header>
        <FormattedMessage id="menu-item.options" />
        {!isWeightedItem && menuPermissions.contentEdit && (
          <AddButton onClick={() => setIsItemOptionsModalOpen(true)}>
            <FormattedMessage id="menu-item.add-option" />
          </AddButton>
        )}
      </Header>
      {!isWeightedItem && (
        <FieldArray<OptionBinding> name="optionBindings">
          {({ fields }) => {
            return (
              <div>
                {fields.map((_, index) => {
                  const { id, option_id } = fields.value[index];
                  return (
                    <OptionBindingHeaderFormSection
                      key={id}
                      optionBindingId={id}
                      option={getOptionFromOptionId(option_id)!}
                      onDeleteOptionBinding={handleDeleteOptionBinding}
                    />
                  );
                })}
              </div>
            );
          }}
        </FieldArray>
      )}
      {isWeightedItem && (
        <StyledInputAlert variant="info">
          {intl.formatMessage({ id: 'menu-item.disabled-for-weighted-items' })}
        </StyledInputAlert>
      )}
      <AddOptionsToItem
        isOpen={isItemOptionsModalOpen}
        onClose={() => setIsItemOptionsModalOpen(false)}
        onAddOptionBindings={handleAddOptionBindings}
      />
    </Root>
  );
};

export default OptionBindingsFormSection;
